import { useMemo, useState, useEffect } from "react";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { COUNTRIES, REGIONS } from "../lib/regions";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

/**
 * Two-step region picker:
 *   1. Country (BE / NL)
 *   2. Province / Brussels-region
 *   3. Municipality
 *
 * Stores the final value as a single string "Gent, Oost-Vlaanderen, BE" so
 * the backend can keep using a free-text regio field.
 */
export default function RegionPicker({ value, onChange, testIdPrefix = "region" }) {
  // Parse incoming value if present
  const parsed = useMemo(() => {
    if (!value) return { country: "", province: "", city: "" };
    const parts = value.split(",").map((s) => s.trim());
    if (parts.length === 3) return { city: parts[0], province: parts[1], country: parts[2] };
    return { country: "", province: "", city: value };
  }, [value]);

  const [country, setCountry] = useState(parsed.country || "");
  const [province, setProvince] = useState(parsed.province || "");
  const [city, setCity] = useState(parsed.city || "");

  useEffect(() => {
    if (country && province && city) {
      onChange?.(`${city}, ${province}, ${country}`);
    } else {
      onChange?.("");
    }
    // eslint-disable-next-line
  }, [country, province, city]);

  const provinces = useMemo(() => {
    if (!country) return [];
    return Object.keys(REGIONS[country]?.provinces || {});
  }, [country]);

  const cities = useMemo(() => {
    if (!country || !province) return [];
    return REGIONS[country]?.provinces?.[province] || [];
  }, [country, province]);

  const handleCountry = (v) => {
    setCountry(v);
    setProvince("");
    setCity("");
  };
  const handleProvince = (v) => {
    setProvince(v);
    setCity("");
  };

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="space-y-2">
        <Label className="text-[13px] uppercase tracking-[0.05em] font-semibold text-oc-textSecondary flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5" /> Land
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {COUNTRIES.map((c) => {
            const active = country === c.code;
            return (
              <button
                type="button"
                key={c.code}
                data-testid={`${testIdPrefix}-country-${c.code}`}
                onClick={() => handleCountry(c.code)}
                className={`p-4 rounded-oc-btn border text-left transition-all flex items-center gap-3 ${
                  active ? "border-oc-primary bg-oc-primary text-white shadow-oc-card" : "border-oc-border bg-white text-oc-primary hover:bg-oc-subtle"
                }`}
              >
                <span className="text-2xl leading-none">{c.flag}</span>
                <div>
                  <p className="font-display font-semibold text-[15px]">{c.label}</p>
                  <p className={`text-[12px] ${active ? "text-white/75" : "text-oc-textSecondary"}`}>
                    {Object.keys(c.provinces).length} provincies
                  </p>
                </div>
                {active && <Check className="w-4 h-4 ml-auto" />}
              </button>
            );
          })}
        </div>
      </div>

      {country && (
        <div className="space-y-2 animate-[fade-in-up_300ms_ease-out_both]">
          <Label className="text-[13px] uppercase tracking-[0.05em] font-semibold text-oc-textSecondary">
            Provincie
          </Label>
          <Select value={province} onValueChange={handleProvince}>
            <SelectTrigger
              data-testid={`${testIdPrefix}-province`}
              className="bg-white border-oc-border h-12 rounded-oc-btn text-[15px]"
            >
              <SelectValue placeholder="Kies je provincie" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              {provinces.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {country && province && (
        <div className="space-y-2 animate-[fade-in-up_300ms_ease-out_both]">
          <Label className="text-[13px] uppercase tracking-[0.05em] font-semibold text-oc-textSecondary">
            Gemeente / stad
          </Label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger
              data-testid={`${testIdPrefix}-city`}
              className="bg-white border-oc-border h-12 rounded-oc-btn text-[15px]"
            >
              <SelectValue placeholder="Kies je gemeente" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              {cities.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {country && province && city && (
        <div className="flex items-center gap-2 text-[13px] text-oc-textSecondary pt-1">
          <ChevronsUpDown className="w-3.5 h-3.5" />
          <span className="font-mono">{city}, {province}, {country}</span>
        </div>
      )}
    </div>
  );
}
